/**
 * Local rolling ETA for Stream / LEC wait overlay (WebApp2).
 * Blends with server estimatedReadyMs when both exist.
 */

export const STREAM_LOAD_ETA_FALLBACK_MS = 12_000;
const MIN_RECORD_MS = 800;
const MAX_RECORD_MS = 120_000;
const MAX_SAMPLES = 20;
const STORAGE_PREFIX = 'streamLoadEta:v1:';

type SampleBucket = {
  samples: number[];
};

const memory = new Map<string, number[]>();

function normalizePart(value: unknown): string {
  return String(value || '').trim().toLowerCase() || '_all';
}

function storageKey(subject: string, stream: string): string {
  return `${STORAGE_PREFIX}${normalizePart(subject)}:${normalizePart(stream)}`;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) {
    return STREAM_LOAD_ETA_FALLBACK_MS;
  }
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[index];
}

function readMemory(subject: string, stream: string): number[] {
  return memory.get(storageKey(subject, stream)) || [];
}

export function peekLocalStreamLoadEtaMs(subject?: string, stream?: string): number | null {
  const samples = readMemory(subject || '_all', stream || '_all');
  if (samples.length < 3) {
    return null;
  }
  const sorted = [...samples].sort((a, b) => a - b);
  return percentile(sorted, 0.8);
}

export function blendStreamLoadEtaMs(serverEtaMs: number | null | undefined, localEtaMs: number | null): number {
  const server = Number(serverEtaMs);
  const hasServer = Number.isFinite(server) && server > 0;
  if (hasServer && localEtaMs && localEtaMs > 0) {
    return Math.round(0.6 * server + 0.4 * localEtaMs);
  }
  if (hasServer) {
    return Math.round(server);
  }
  if (localEtaMs && localEtaMs > 0) {
    return Math.round(localEtaMs);
  }
  return STREAM_LOAD_ETA_FALLBACK_MS;
}

export function hydrateStreamLoadEta(subject: string, stream: string): number | null {
  const key = storageKey(subject, stream);
  if (memory.has(key)) {
    return peekLocalStreamLoadEtaMs(subject, stream);
  }
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) as SampleBucket : null;
    const samples = Array.isArray(parsed?.samples)
      ? parsed.samples.filter((value) => Number.isFinite(value) && value >= MIN_RECORD_MS && value <= MAX_RECORD_MS)
      : [];
    memory.set(key, samples.slice(-MAX_SAMPLES));
  } catch {
    memory.set(key, []);
  }
  return peekLocalStreamLoadEtaMs(subject, stream);
}

export function recordStreamLoadDuration(subject: string, stream: string, durationMs: number): void {
  if (!Number.isFinite(durationMs) || durationMs < MIN_RECORD_MS || durationMs > MAX_RECORD_MS) {
    return;
  }
  const key = storageKey(subject, stream);
  const next = [...readMemory(subject, stream), Math.round(durationMs)].slice(-MAX_SAMPLES);
  memory.set(key, next);
  try {
    localStorage.setItem(key, JSON.stringify({ samples: next } satisfies SampleBucket));
  } catch {
    // ignore quota / private mode
  }
}

export function remainingStreamLoadEtaMs(startedAtMs: number, etaMs: number, nowMs = Date.now()): number {
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(etaMs) || etaMs <= 0) {
    return 0;
  }
  return Math.max(0, etaMs - (nowMs - startedAtMs));
}

export function streamLoadProgressPercent(startedAtMs: number, etaMs: number, nowMs = Date.now()): number {
  if (!Number.isFinite(etaMs) || etaMs <= 0) {
    return 8;
  }
  const elapsed = Math.max(0, nowMs - startedAtMs);
  return Math.min(92, Math.max(4, Math.round((elapsed / etaMs) * 100)));
}
